import { Pin, Plus, X } from "lucide-mui";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useState } from "react";

interface LabelsCellProps {
  labels: Record<string, string>;
  selectedLabelKeys: string[];
  onPin: (key: string) => void;
  /** Leave out for read-only labels (they are edited elsewhere). */
  onRemove?: (key: string, e: React.MouseEvent) => void;
  onOpenAddLabel?: (anchor: HTMLElement) => void;
}

/** The labels not pinned as columns, as a "N labels" chip; each can be pinned. */
export default function LabelsCell({
  labels,
  selectedLabelKeys,
  onPin,
  onRemove,
  onOpenAddLabel,
}: LabelsCellProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const unpinned = Object.entries(labels).filter(([k]) => !selectedLabelKeys.includes(k));

  const tooltipContent =
    unpinned.length === 0 ? null : (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        {unpinned.map(([k, v]) => (
          <Typography key={k} variant="caption" sx={{ whiteSpace: "nowrap" }}>
            {k}={v}
          </Typography>
        ))}
      </Box>
    );

  return (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
      {unpinned.length > 0 && (
        <Tooltip title={tooltipContent}>
          <Chip
            label={`${unpinned.length} label${unpinned.length === 1 ? "" : "s"}`}
            size="small"
            clickable
            onClick={(e) => setAnchorEl(e.currentTarget)}
          />
        </Tooltip>
      )}
      {onOpenAddLabel && (
        <Tooltip title="Add label">
          <IconButton size="small" onClick={(e) => onOpenAddLabel(e.currentTarget)}>
            <Plus fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, maxWidth: 360 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {unpinned.map(([k, v]) => (
              <Tooltip key={k} title="Pin as column">
                <Chip
                  label={`${k}=${v}`}
                  size="small"
                  icon={
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPin(k);
                      }}
                      sx={{ p: 0.25 }}
                    >
                      <Pin fontSize="small" />
                    </IconButton>
                  }
                  onDelete={onRemove ? (e) => onRemove(k, e) : undefined}
                  deleteIcon={onRemove ? <X fontSize="small" /> : undefined}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
